// ══════════════════════════════════════════════════════════════════════════════
// UNIFIED POST MODAL - Modal pleine page pour créer des publications
// Utilisé par: Groupes Privés, Réseau Social Public, Pros, Particuliers
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Image, Video, FileText, BarChart3, Calendar, CheckSquare, 
  Paperclip, Sparkles, Send, Save, Trash2, ChevronDown, ChevronRight,
  Camera, Mic, MapPin, Users, Lock, Globe, Plus, Smartphone,
  RefreshCw, Clock, AlertCircle, User, Store
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ══════════════════════════════════════════════════════════════════════════════
// TYPES DE POSTS SELON LE CONTEXTE
// ══════════════════════════════════════════════════════════════════════════════

const POST_TYPES = {
  // Commun à tous
  text: { id: 'text', label: 'Texte', icon: FileText, color: '#6B7280', contexts: ['private', 'public'] },
  photo: { id: 'photo', label: 'Photo', icon: Image, color: '#10B981', contexts: ['private', 'public'] },
  video: { id: 'video', label: 'Vidéo', icon: Video, color: '#8B5CF6', contexts: ['private', 'public'] },
  poll: { id: 'poll', label: 'Sondage', icon: BarChart3, color: '#F59E0B', contexts: ['private', 'public'] },
  event: { id: 'event', label: 'Événement', icon: Calendar, color: '#EC4899', contexts: ['private', 'public'] },
  
  // Groupes privés uniquement
  task: { id: 'task', label: 'Tâche', icon: CheckSquare, color: '#EF4444', contexts: ['private'] },
  document: { id: 'document', label: 'Document', icon: Paperclip, color: '#3B82F6', contexts: ['private'] },
  
  // Réseau public uniquement
  reel: { id: 'reel', label: 'Réel', icon: Smartphone, color: '#EC4899', contexts: ['public'] },
  avant_apres: { id: 'avant_apres', label: 'Avant/Après', icon: RefreshCw, color: '#F97316', contexts: ['public', 'pro'] },
  article: { id: 'article', label: 'Article', icon: FileText, color: '#1E3A5F', contexts: ['public', 'pro'] },
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function UnifiedPostModal({ 
  isOpen, 
  onClose, 
  onPublish,
  onAskAbra,
  context = 'private', // 'private' | 'public' | 'pro'
  contextId = null, // ID du groupe, team, etc.
  contextName = '', // Nom du groupe pour affichage
  token,
  currentUser = {},
  initialPostType = null, // Type de post pré-sélectionné
  initialContent = '' // Contenu pré-rempli
}) {
  // États
  const [step, setStep] = useState(initialPostType ? 2 : 1); // Skip step 1 if type is pre-selected
  const [postType, setPostType] = useState(initialPostType);
  const [content, setContent] = useState(initialContent);
  const [title, setTitle] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [visibility, setVisibility] = useState('group'); // group | public | friends
  const [loading, setLoading] = useState(false);
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);
  
  // États sondage
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollMultiple, setPollMultiple] = useState(false);
  
  // États événement
  const [eventTitle, setEventTitle] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  
  // États tâche
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');
  const [taskAssignees, setTaskAssignees] = useState([]);

  // Destinations de publication
  const [destinations, setDestinations] = useState(['reseau']);

  // Refs
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const toggleDestination = (dest) => {
    if (dest === 'reseau') return; // Réseau toujours activé
    setDestinations(prev =>
      prev.includes(dest) ? prev.filter(d => d !== dest) : [...prev, dest]
    );
  };

  // Reset au changement d'ouverture
  useEffect(() => {
    if (isOpen) {
      // Si un type initial est fourni, sauter l'étape 1
      if (initialPostType) {
        setStep(2);
        setPostType(initialPostType);
        setContent(initialContent || '');
      } else {
        setStep(1);
        setPostType(null);
        setContent('');
      }
      setTitle('');
      setMediaFiles([]);
      setMediaPreviews([]);
      setPollQuestion('');
      setPollOptions(['', '']);
      setEventTitle('');
      setEventDate('');
      setEventTime('');
      setEventLocation('');
      setTaskTitle('');
      setTaskDescription('');
      setTaskDueDate('');
      setTaskPriority('medium');
      setTaskAssignees([]);
    }
  }, [isOpen, initialPostType, initialContent]);

  // Focus textarea quand on passe à l'étape 2
  useEffect(() => {
    if (step === 2 && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [step]);

  // Filtrer les types de posts selon le contexte
  const availableTypes = Object.values(POST_TYPES).filter(type => 
    type.contexts.includes(context) || type.contexts.includes('pro') && context === 'pro'
  );

  // Gérer l'upload de fichiers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const newPreviews = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({
          url: e.target.result,
          type: file.type.startsWith('video/') ? 'video' : 'image',
          name: file.name
        });
        if (newPreviews.length === files.length) {
          setMediaPreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
    setMediaFiles(prev => [...prev, ...files]);
  };

  // Supprimer un média
  const removeMedia = (index) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
    setMediaPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Ajouter une option de sondage
  const addPollOption = () => {
    if (pollOptions.length < 6) {
      setPollOptions([...pollOptions, '']);
    }
  };

  // Supprimer une option de sondage
  const removePollOption = (index) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

  // Valider et publier
  const handlePublish = async () => {
    // Validation
    if (postType === 'poll' && (!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2)) {
      toast.error('Le sondage doit avoir une question et au moins 2 options');
      return;
    }
    if (postType === 'event' && (!eventTitle.trim() || !eventDate)) {
      toast.error('L\'événement doit avoir un titre et une date');
      return;
    }
    if (postType === 'task' && !taskTitle.trim()) {
      toast.error('La tâche doit avoir un titre');
      return;
    }
    if (!postType) {
      toast.error('Veuillez choisir un type de publication');
      return;
    }

    setLoading(true);
    
    try {
      const postData = {
        type: postType,
        content: content.trim(),
        title: title.trim(),
        files: mediaFiles,
        visibility,
        destinations,
        poll: postType === 'poll' ? {
          question: pollQuestion,
          options: pollOptions.filter(o => o.trim()),
          multiple: pollMultiple
        } : null,
        event: postType === 'event' ? {
          title: eventTitle,
          date: `${eventDate}T${eventTime || '12:00'}`,
          location: eventLocation
        } : null,
        task: postType === 'task' ? {
          title: taskTitle,
          description: taskDescription,
          due_date: taskDueDate,
          priority: taskPriority,
          assignees: taskAssignees
        } : null
      };
      
      if (onPublish) {
        await onPublish(postData);
      }
      
      toast.success('Publication créée !');
      onClose();
    } catch (error) {
      console.error('Erreur publication:', error);
      toast.error('Erreur lors de la publication');
    } finally {
      setLoading(false);
    }
  };

  // État pour le loading Abra
  const [abraLoading, setAbraLoading] = useState(false);

  // Demander à Abra - Appelle l'API d'assistance IA
  const handleAskAbra = async () => {
    if (!postType) {
      toast.info('Choisis d\'abord un type de publication !');
      return;
    }
    
    setAbraLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/ai/post-assist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          post_type: postType,
          current_content: content,
          group_name: contextName,
          action: 'suggest'
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Appliquer les suggestions selon le type de post
        if (postType === 'poll' && data.poll_question) {
          setPollQuestion(data.poll_question);
          if (data.poll_options && data.poll_options.length > 0) {
            setPollOptions(data.poll_options);
          }
          toast.success('✨ Abra a suggéré un sondage !');
        } else if (postType === 'event' && data.event_title) {
          setEventTitle(data.event_title);
          if (data.event_description) {
            setContent(data.event_description);
          }
          toast.success('✨ Abra a suggéré un événement !');
        } else if (postType === 'task' && data.task_title) {
          setTaskTitle(data.task_title);
          if (data.task_description) {
            setTaskDescription(data.task_description);
          }
          toast.success('✨ Abra a suggéré une tâche !');
        } else if (data.suggestion) {
          setContent(data.suggestion);
          toast.success('✨ Abra a une idée pour toi !');
        } else {
          toast.info('Abra n\'a pas eu d\'inspiration cette fois...');
        }
      } else {
        toast.error('Abra est occupé, réessaie !');
      }
    } catch (error) {
      console.error('Erreur Abra:', error);
      toast.error('Impossible de contacter Abra');
    } finally {
      setAbraLoading(false);
    }
  };

  // Sauvegarder en brouillon
  const handleSaveDraft = () => {
    toast.success('Brouillon sauvegardé');
    // TODO: Sauvegarder en local ou en base
  };

  if (!isOpen) return null;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10003] bg-white flex flex-col"
    >
      {/* Header */}
      <header 
        className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between"
        style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}
      >
        <button 
          onClick={step === 2 ? () => setStep(1) : onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
        
        <h1 className="text-base font-semibold text-gray-900">
          {step === 1 ? 'Nouvelle publication' : postType ? POST_TYPES[postType]?.label : 'Publication'}
        </h1>
        
        <div className="w-9" /> {/* Spacer */}
      </header>

      {/* Contenu */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          {step === 1 ? (
            // ══════════════════════════════════════════════════════════════════
            // ÉTAPE 1 : CHOIX DU TYPE
            // ══════════════════════════════════════════════════════════════════
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="p-4"
            >
              {/* Info contexte */}
              {contextName && (
                <div className="mb-4 p-3 bg-orange-50 rounded-xl border border-orange-100">
                  <p className="text-sm text-orange-800">
                    Publication dans <span className="font-semibold">{contextName}</span>
                  </p>
                </div>
              )}

              <p className="text-gray-600 mb-4">Que veux-tu publier ?</p>

              {/* Grille des types */}
              <div className="grid grid-cols-3 gap-3">
                {availableTypes.map((type) => (
                  <motion.button
                    key={type.id}
                    onClick={() => {
                      setPostType(type.id);
                      setStep(2);
                    }}
                    className="flex flex-col items-center gap-2 p-4 bg-white border border-gray-200 rounded-xl hover:border-orange-300 hover:shadow-sm transition-all"
                    whileTap={{ scale: 0.95 }}
                  >
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${type.color}15` }}
                    >
                      <type.icon className="w-6 h-6" style={{ color: type.color }} />
                    </div>
                    <span className="text-xs font-medium text-gray-700">{type.label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          ) : (
            // ══════════════════════════════════════════════════════════════════
            // ÉTAPE 2 : COMPOSITION
            // ══════════════════════════════════════════════════════════════════
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-4"
            >
              {/* Auteur */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                  {currentUser?.avatar ? (
                    <img src={currentUser.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-white font-semibold text-sm">
                      {currentUser?.name?.charAt(0) || 'M'}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{currentUser?.name || 'Moi'}</p>
                  
                  {/* Sélecteur de visibilité */}
                  <button 
                    onClick={() => setShowVisibilityMenu(!showVisibilityMenu)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    {visibility === 'group' && <Users className="w-3 h-3" />}
                    {visibility === 'public' && <Globe className="w-3 h-3" />}
                    {visibility === 'friends' && <Lock className="w-3 h-3" />}
                    <span>
                      {visibility === 'group' && 'Groupe'}
                      {visibility === 'public' && 'Public'}
                      {visibility === 'friends' && 'Amis'}
                    </span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Zone de texte principale */}
              {['text', 'photo', 'video', 'document'].includes(postType) && (
                <textarea
                  ref={textareaRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Quoi de neuf ?"
                  className="w-full min-h-[120px] text-gray-900 placeholder-gray-400 resize-none focus:outline-none text-base"
                />
              )}

              {/* ════════════════════════════════════════════════════════════════
                  FORMULAIRE SONDAGE
                  ════════════════════════════════════════════════════════════════ */}
              {postType === 'poll' && (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={pollQuestion}
                    onChange={(e) => setPollQuestion(e.target.value)}
                    placeholder="Pose ta question..."
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  
                  <div className="space-y-2">
                    {pollOptions.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...pollOptions];
                            newOptions[index] = e.target.value;
                            setPollOptions(newOptions);
                          }}
                          placeholder={`Option ${index + 1}`}
                          className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                        {pollOptions.length > 2 && (
                          <button 
                            onClick={() => removePollOption(index)}
                            className="p-2 text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {pollOptions.length < 6 && (
                    <button 
                      onClick={addPollOption}
                      className="flex items-center gap-2 text-orange-600 text-sm font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter une option
                    </button>
                  )}
                  
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={pollMultiple}
                      onChange={(e) => setPollMultiple(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    Autoriser plusieurs réponses
                  </label>
                </div>
              )}

              {/* ════════════════════════════════════════════════════════════════
                  FORMULAIRE ÉVÉNEMENT
                  ════════════════════════════════════════════════════════════════ */}
              {postType === 'event' && (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={eventTitle}
                    onChange={(e) => setEventTitle(e.target.value)}
                    placeholder="Nom de l'événement"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Date</label>
                      <input
                        type="date"
                        value={eventDate}
                        onChange={(e) => setEventDate(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Heure</label>
                      <input
                        type="time"
                        value={eventTime}
                        onChange={(e) => setEventTime(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={eventLocation}
                      onChange={(e) => setEventLocation(e.target.value)}
                      placeholder="Ajouter un lieu"
                      className="flex-1 text-gray-900 placeholder-gray-400 focus:outline-none"
                    />
                  </div>
                  
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Description (optionnel)"
                    className="w-full min-h-[80px] px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* ════════════════════════════════════════════════════════════════
                  FORMULAIRE TÂCHE
                  ════════════════════════════════════════════════════════════════ */}
              {postType === 'task' && (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={taskTitle}
                    onChange={(e) => setTaskTitle(e.target.value)}
                    placeholder="Titre de la tâche"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  
                  <textarea
                    value={taskDescription}
                    onChange={(e) => setTaskDescription(e.target.value)}
                    placeholder="Description (optionnel)"
                    className="w-full min-h-[80px] px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Échéance</label>
                      <input
                        type="date"
                        value={taskDueDate}
                        onChange={(e) => setTaskDueDate(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Priorité</label>
                      <select
                        value={taskPriority}
                        onChange={(e) => setTaskPriority(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="low">🟢 Basse</option>
                        <option value="medium">🟡 Moyenne</option>
                        <option value="high">🔴 Haute</option>
                        <option value="urgent">🚨 Urgente</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* TODO: Sélecteur de membres pour assignation */}
                  <button className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-600 w-full">
                    <Users className="w-5 h-5 text-gray-400" />
                    <span>Assigner à des membres</span>
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </button>
                </div>
              )}

              {/* ════════════════════════════════════════════════════════════════
                  PRÉVISUALISATION MÉDIAS
                  ════════════════════════════════════════════════════════════════ */}
              {mediaPreviews.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {mediaPreviews.map((preview, index) => (
                    <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                      {preview.type === 'video' ? (
                        <video src={preview.url} className="w-full h-full object-cover" />
                      ) : (
                        <img src={preview.url} alt="" className="w-full h-full object-cover" />
                      )}
                      <button
                        onClick={() => removeMedia(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* ════════════════════════════════════════════════════════════════
                  BOUTONS D'AJOUT RAPIDE
                  ════════════════════════════════════════════════════════════════ */}
              {['text', 'photo', 'video'].includes(postType) && (
                <div className="mt-4 flex items-center gap-2 py-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500">Ajouter :</span>
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <Image className="w-5 h-5 text-green-600" />
                  </button>
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <Camera className="w-5 h-5 text-blue-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <MapPin className="w-5 h-5 text-red-500" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          FOOTER - BOUTONS D'ACTION
          ══════════════════════════════════════════════════════════════════════ */}
      {step === 2 && (
        <div 
          className="bg-white border-t border-gray-200 px-4 py-3 space-y-3"
          style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
        >
          {/* Destinations — visible uniquement pour les pros */}
          {(context === 'pro' || currentUser?.is_pro || currentUser?.profile_type === 'pro') && (
            <div className="flex items-center gap-2 py-2 border-t border-gray-100 flex-wrap">
              <span className="text-xs text-gray-500 font-medium">Publier sur :</span>

              {/* Réseau — toujours actif */}
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                <Globe size={11} />
                Réseau
              </span>

              {/* Boutique — toggle */}
              <button
                type="button"
                onClick={() => toggleDestination('boutique')}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  destinations.includes('boutique')
                    ? 'bg-orange-100 text-orange-700 border-orange-300'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-300'
                }`}
              >
                <Store size={11} />
                Ma boutique
              </button>

              {/* Réseaux sociaux — à venir */}
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed" title="Bientôt disponible">
                <Globe size={11} />
                Réseaux sociaux
                <span className="text-[9px] bg-gray-200 text-gray-500 px-1 rounded">Bientôt</span>
              </span>
            </div>
          )}

          {/* Bouton Publier */}
          <button
            onClick={handlePublish}
            disabled={loading}
            className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Send className="w-5 h-5" />
                Publier
              </>
            )}
          </button>
          
          {/* Boutons secondaires */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleAskAbra}
              disabled={abraLoading}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-sm font-medium rounded-xl hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 transition-all"
              data-testid="ask-abra-btn"
            >
              {abraLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {abraLoading ? 'Abra réfléchit...' : '✨ Demander à Abra'}
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveDraft}
                className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-xl text-sm font-medium transition-colors"
              >
                <Save className="w-4 h-4" />
                Brouillon
              </button>
              
              <button
                onClick={onClose}
                className="flex items-center gap-1 px-3 py-2 text-red-500 hover:bg-red-50 rounded-xl text-sm font-medium transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>,
    document.body
  );
}
